import * as THREE from "three";
import { projectHotspot } from "./project-hotspot.js";

/**
 * @typedef {object} HotspotRuntime
 * @property {string} key
 * @property {string} cardName
 * @property {HTMLButtonElement} element
 * @property {import("three").Object3D | null} anchor
 * @property {import("three").Object3D | null} cameraPosition
 * @property {import("three").Object3D | null} cameraTarget
 * @property {() => void} handleClick
 */

export function createHotspotController({
  configs,
  onSelect,
  isExperienceReady,
  onPosition,
  targetDocument = document,
}) {
  const worldPosition = new THREE.Vector3();
  const projectedPosition = new THREE.Vector3();
  /** @type {HotspotRuntime[]} */
  const hotspots = [];
  let root = null;

  function setVisibility(element, visible) {
    element.style.visibility = visible ? "visible" : "hidden";
    element.style.pointerEvents = visible ? "auto" : "none";
  }

  function reportMissingObject(key, role, objectName) {
    console.error(
      `[Hotspots] Missing ${role} in GLTF scene for '${key}' (looked for ${objectName})`,
    );
  }

  return {
    initialize(sceneRoot) {
      root = sceneRoot;

      configs.forEach(({ key, cardName = key, label }) => {
        const element = targetDocument.createElement("button");
        element.className = `hotspot hotspot--${cardName}`;
        element.type = "button";
        element.ariaLabel = label;
        element.title = label;
        targetDocument.body.appendChild(element);

        const anchorName = `anchor_${key}`;
        const cameraName = `camera_${key}`;
        const cameraTargetName = `cameraTarget_${key}`;
        const anchor = sceneRoot.getObjectByName(anchorName) ?? null;
        const cameraPosition = sceneRoot.getObjectByName(cameraName) ?? null;
        const cameraTarget =
          sceneRoot.getObjectByName(cameraTargetName) ?? null;

        if (!anchor) reportMissingObject(key, "anchor empty", anchorName);
        if (!cameraPosition) {
          reportMissingObject(key, "camera position empty", cameraName);
        }
        if (!cameraTarget) {
          reportMissingObject(key, "camera target empty", cameraTargetName);
        }

        const hotspot = {
          key,
          cardName,
          element,
          anchor,
          cameraPosition,
          cameraTarget,
        };
        const handleClick = () => onSelect(key);
        hotspot.handleClick = handleClick;
        element.addEventListener("click", handleClick);
        hotspots.push(hotspot);
      });
    },
    getByKey(key) {
      return hotspots.find((hotspot) => hotspot.key === key) ?? null;
    },
    updateFrame(camera, viewport) {
      if (!camera || !root) return;

      camera.updateMatrixWorld();
      root.updateMatrixWorld();

      hotspots.forEach(({ element, anchor }) => {
        if (!isExperienceReady() || !anchor) {
          setVisibility(element, false);
          return;
        }

        anchor.getWorldPosition(worldPosition);
        projectedPosition.copy(worldPosition).project(camera);
        const projection = projectHotspot(projectedPosition, viewport);
        setVisibility(element, projection.visible);

        if (!projection.visible) return;

        element.style.transform =
          `translate(-50%, -50%) translate(${projection.x}px, ${projection.y}px)`;
        onPosition(element, projection.x, projection.y, viewport);
      });
    },
    destroy() {
      hotspots.forEach(({ element, handleClick }) => {
        element.removeEventListener("click", handleClick);
        element.remove();
      });
      hotspots.length = 0;
      root = null;
    },
  };
}
